// Script commun à toutes les pages : menu, sous-menus, formulaires, horaires.

import { CLOSURES, closedReason } from "../src/data/closures.js";
import { DEALER } from "../src/data/dealer.js";
import { FORMS } from "../src/data/forms.js";

const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;

// ---------------------------------------------------------------- menu

const header = document.querySelector(".site-header");
const toggle = header?.querySelector(".menu-toggle");
const groups = [...document.querySelectorAll(".menu-group")];
const desktop = matchMedia("(min-width: 1080px)"); // même seuil que le menu replié de css/site.css

function setMenu(open) {
  if (!toggle) return;
  toggle.setAttribute("aria-expanded", String(open));
  toggle.querySelector(".menu-toggle-label").textContent = open ? "Fermer" : "Menu";
  document.body.classList.toggle("menu-open", open);
}

function setGroup(group, open) {
  group.classList.toggle("is-open", open);
  group.querySelector(".menu-button").setAttribute("aria-expanded", String(open));
}
const closeGroups = (except) => groups.forEach((g) => g !== except && setGroup(g, false));

toggle?.addEventListener("click", () => setMenu(toggle.getAttribute("aria-expanded") !== "true"));
desktop.addEventListener("change", () => {
  setMenu(false);
  closeGroups();
});

for (const g of groups) {
  const button = g.querySelector(".menu-button");
  button.addEventListener("click", () => {
    // Souris sur ordinateur : le survol a déjà ouvert le sous-menu, le clic ne doit
    // pas le refermer (beaucoup survolent puis cliquent). Clavier : ouvre / ferme.
    const open = (desktop.matches && g.matches(":hover")) || !g.classList.contains("is-open");
    closeGroups(g);
    setGroup(g, open);
  });
  // Survol à la souris (ordinateur) avec un petit délai à la fermeture.
  let timer;
  g.addEventListener("pointerenter", (e) => {
    if (e.pointerType !== "mouse" || !desktop.matches) return;
    clearTimeout(timer);
    closeGroups(g);
    setGroup(g, true);
  });
  g.addEventListener("pointerleave", (e) => {
    if (e.pointerType !== "mouse" || !desktop.matches) return;
    timer = setTimeout(() => setGroup(g, false), 180);
  });
  g.addEventListener("focusout", (e) => {
    if (desktop.matches && !g.contains(e.relatedTarget)) setGroup(g, false);
  });
}

document.addEventListener("click", (e) => {
  if (!e.target.closest(".menu-group")) closeGroups();
  if (e.target.closest(".nav a")) setMenu(false);
});
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  const open = groups.find((g) => g.classList.contains("is-open"));
  if (open) {
    setGroup(open, false);
    open.querySelector(".menu-button").focus();
  } else if (document.body.classList.contains("menu-open")) {
    setMenu(false);
    toggle.focus();
  }
});

// ---------------------------------------------------------------- horaires : ouvert / fermé

const DAYS = ["", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const hour = (x) => {
  const h = Math.floor(x);
  const m = Math.round((x - h) * 60);
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
};

function parisNow(date = new Date()) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Paris", weekday: "short", hour: "numeric", minute: "numeric", month: "numeric", day: "numeric", year: "numeric", hourCycle: "h23" })
      .formatToParts(date)
      .map((x) => [x.type, x.value]),
  );
  const day = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(p.weekday) + 1;
  return { day, year: Number(p.year), month: Number(p.month), date: Number(p.day), time: Number(p.hour) + Number(p.minute) / 60 };
}

// Jour k jours après la date de Paris donnée : jour de la semaine, mois, AAAA-MM-JJ.
function dayAfter(now, k) {
  const d = new Date(Date.UTC(now.year, now.month - 1, now.date + k));
  return { day: ((d.getUTCDay() + 6) % 7) + 1, month: d.getUTCMonth() + 1, iso: d.toISOString().slice(0, 10) };
}

// Plages d'ouverture d'un jour, en tenant compte des jours fériés et fermetures.
function slotsFor(schedule, day, month, iso) {
  if (iso && closedReason(iso)) return [];
  const e = schedule.days.find((d) => d.day === day);
  return e && !e.except?.includes(month) ? e.slots : [];
}

export function openText(schedule, date = new Date()) {
  const now = parisNow(date);
  const today = dayAfter(now, 0);
  const open = slotsFor(schedule, now.day, now.month, today.iso).find(([a, b]) => now.time >= a && now.time < b);
  if (open) return { open: true, text: `Ouvert jusqu'à ${hour(open[1])}` };
  const reason = closedReason(today.iso);
  for (let k = 0; k < 21; k++) {
    const d = dayAfter(now, k);
    const slot = slotsFor(schedule, d.day, d.month, d.iso).find(([a]) => k > 0 || a > now.time);
    if (slot) {
      const when = k === 0 ? "" : k === 1 ? "demain " : k < 7 ? `${DAYS[d.day]} ` : `le ${new Date(`${d.iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} `;
      return { open: false, text: `Fermé${reason ? ` (${reason.toLowerCase()})` : ""}, ouvre ${when}à ${hour(slot[0])}` };
    }
  }
  return { open: false, text: "Fermé" };
}

// Bandeau d'annonce pour une fermeture exceptionnelle en cours ou dans les 30 jours.
{
  const today = dayAfter(parisNow(), 0).iso;
  const soon = dayAfter(parisNow(), 30).iso;
  const c = CLOSURES.find((x) => x.to >= today && x.from <= soon);
  if (c) {
    const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    const bar = document.createElement("p");
    bar.className = "closure-banner";
    bar.setAttribute("role", "note");
    bar.textContent = c.from === c.to ? `${c.label} le ${fmt(c.from)}.` : `${c.label} du ${fmt(c.from)} au ${fmt(c.to)} inclus.`;
    document.querySelector(".site-header")?.after(bar);
  }
}

function refreshStatus() {
  for (const el of document.querySelectorAll("[data-open-status]")) {
    const { open, text } = openText(JSON.parse(el.dataset.hours));
    el.classList.toggle("is-open", open);
    if (el.hasAttribute("data-compact")) {
      // Pastille de l'en-tête : texte en infobulle et pour les lecteurs d'écran.
      el.title = text;
      el.innerHTML = `<span class="sr-only">${text}. </span>`;
    } else {
      el.textContent = text;
      el.hidden = false;
    }
  }
}
refreshStatus();
setInterval(refreshStatus, 60_000);

// ---------------------------------------------------------------- formulaires
//
// Chaque formulaire est décrit dans src/data/forms.js et envoyé à
// /api/demande (server/leads.js). Sans JavaScript, il part tel quel ; avec,
// il est contrôlé ici, envoyé en arrière-plan, puis remplacé par un
// récapitulatif. En cas d'échec, les champs restent remplis.

const escHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const IDENTITY = new Set(["prenom", "nom", "email", "telephone"]);
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const longDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

// Prochains jours ouverts (à partir de demain) selon les horaires du magasin ou de l'atelier.
function nextOpenDays(schedule, count) {
  const out = [];
  const base = new Date();
  base.setHours(12, 0, 0, 0);
  for (let i = 1; out.length < count && i < 60; i++) {
    const d = new Date(base.getTime() + i * 864e5);
    if (slotsFor(schedule, ((d.getDay() + 6) % 7) + 1, d.getMonth() + 1, isoDay(d)).length) out.push(d);
  }
  return out;
}

// Remplace le champ date de repli par des pastilles « sam. 4 oct. » à cocher.
function setupDays(box) {
  const fallback = box.querySelector(".days-fallback");
  if (!fallback) return;
  const max = Number(box.dataset.max) || 3;
  const wrap = document.createElement("div");
  wrap.className = "chips chips-days";
  wrap.innerHTML = nextOpenDays(JSON.parse(box.dataset.hours), 12)
    .map(
      (d) =>
        `<label class="chip chip-day"><input type="checkbox" name="${box.dataset.days}" value="${isoDay(d)}"><span><small>${d.toLocaleDateString("fr-FR", { weekday: "short" })}</small> ${d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span></label>`,
    )
    .join("");
  fallback.replaceWith(wrap);
  const limit = () => {
    const n = wrap.querySelectorAll("input:checked").length;
    wrap.querySelectorAll("input:not(:checked)").forEach((i) => (i.disabled = n >= max));
  };
  wrap.addEventListener("change", limit);
  box.closest("form").addEventListener("reset", () => setTimeout(limit));
}

function message(el) {
  if (el.type === "tel") el.setCustomValidity(el.value && el.value.replace(/\D/g, "").length < 10 ? "tel" : "");
  const v = el.validity;
  if (v.valid) return "";
  if (v.valueMissing)
    return el.type === "checkbox" ? "Cette case est obligatoire." : el.tagName === "SELECT" ? "Choisissez une option." : "Ce champ est obligatoire.";
  if (el.type === "email") return "Adresse e-mail invalide (exemple : nom@domaine.fr).";
  if (el.type === "tel") return "Numéro de téléphone incomplet.";
  if (v.rangeUnderflow) return el.type === "date" ? "Choisissez une date à venir." : `Valeur minimale : ${el.min}.`;
  if (v.rangeOverflow) return `Valeur maximale : ${el.max}.`;
  if (v.badInput) return "Saisissez un nombre.";
  return "Valeur invalide.";
}

// Un « champ » = un bloc [data-field] (un contrôle, ou un groupe de pastilles).
function unitMessage(unit) {
  if (unit.matches(".field-days")) {
    const fallback = unit.querySelector(".days-fallback");
    if (fallback) return message(fallback) || (unit.hasAttribute("data-required") && !fallback.value ? "Choisissez une date." : "");
    return unit.hasAttribute("data-required") && !unit.querySelector("input:checked") ? "Choisissez au moins un jour." : "";
  }
  if (unit.matches(".field-choice"))
    return unit.querySelector("input[required]") && !unit.querySelector("input:checked") ? "Choisissez une option." : "";
  const el = unit.querySelector("input, select, textarea");
  return el ? message(el) : "";
}

function showError(unit, msg) {
  const err = unit.querySelector(".field-error");
  if (err) {
    err.textContent = msg;
    err.hidden = !msg;
  }
  for (const el of unit.querySelectorAll("input, select, textarea"))
    if (msg) el.setAttribute("aria-invalid", "true");
    else el.removeAttribute("aria-invalid");
  return !msg;
}
const check = (unit) => showError(unit, unitMessage(unit));
const focusUnit = (unit) => unit.querySelector("input:not([disabled]), select, textarea")?.focus();

function display(field, value) {
  if (value == null || value === "" || (Array.isArray(value) && !value.length)) return "";
  if (field.type === "days") return [].concat(value).map(longDay).join(", ");
  if (field.type === "checkbox") return "";
  if (field.type === "number") return Number(value).toLocaleString("fr-FR");
  return String(value);
}

function confirmation(form, payload, result) {
  const def = FORMS[form.dataset.form];
  const recap = def.fields
    .filter((f) => !IDENTITY.has(f.name))
    .map((f) => [f.label, display(f, payload[f.name])])
    .filter(([, v]) => v);
  const panel = document.createElement("div");
  panel.className = "form-done";
  panel.tabIndex = -1;
  panel.innerHTML = `
    <span class="form-done-mark" aria-hidden="true"></span>
    <h3 class="form-done-title">${payload.prenom ? `Merci ${escHtml(payload.prenom)}, c'est envoyé` : "C'est envoyé"}</h3>
    <p>${escHtml(def.next)}</p>
    ${recap.length ? `<dl class="form-recap">${recap.map(([k, v]) => `<div><dt>${escHtml(k)}</dt><dd>${escHtml(v)}</dd></div>`).join("")}</dl>` : ""}
    ${result.autoreply ? `<p class="small muted">Un e-mail de confirmation part vers ${escHtml(payload.email)}.</p>` : ""}
    ${result.dryRun ? `<p class="small form-dry">Mode essai du serveur local : rien n'a été envoyé, la demande est enregistrée dans .leads/.</p>` : ""}
    ${form.dataset.form === "newsletter" ? "" : `<p><button type="button" class="btn btn-small form-again">Faire une autre demande</button></p>`}`;
  form.hidden = true;
  form.before(panel);
  panel.focus();
  panel.querySelector(".form-again")?.addEventListener("click", () => {
    form.reset();
    form.querySelector(".form-status").textContent = "";
    form.hidden = false;
    panel.remove();
    focusUnit(form.querySelector("[data-field]"));
  });
}

for (const form of document.querySelectorAll("form[data-form]")) {
  const status = form.querySelector(".form-status");
  const button = form.querySelector(".form-submit");
  const label = button.querySelector(".form-submit-label");
  const idle = label.textContent;
  const units = () => [...form.querySelectorAll("[data-field]")];
  const unitOf = (el) => el.closest("[data-field]");
  let tried = false;
  let busy = false;

  form.querySelectorAll("[data-days]").forEach(setupDays);
  const tomorrow = isoDay(new Date(Date.now() + 864e5));
  form.querySelectorAll('input[type="date"]').forEach((d) => (d.min = tomorrow));

  const recheck = (e) => tried && unitOf(e.target) && check(unitOf(e.target));
  form.addEventListener("input", recheck);
  form.addEventListener("change", recheck);

  const setBusy = (on) => {
    busy = on;
    button.disabled = on;
    button.classList.toggle("is-busy", on);
    button.setAttribute("aria-busy", String(on));
    label.textContent = on ? "Envoi en cours…" : idle;
  };

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (busy) return;
    tried = true;
    status.classList.remove("is-error");
    const invalid = units().filter((u) => !check(u));
    if (invalid.length) {
      status.textContent = invalid.length === 1 ? "Un champ est à corriger." : `${invalid.length} champs sont à corriger.`;
      focusUnit(invalid[0]);
      return;
    }

    const payload = {};
    for (const [k, v] of new FormData(form)) payload[k] = k in payload ? [].concat(payload[k], v) : v;
    form.querySelectorAll("[data-days]").forEach((b) => (payload[b.dataset.days] = [].concat(payload[b.dataset.days] ?? []).filter(Boolean)));
    payload._t = Math.round(performance.now());
    payload._page = location.pathname;

    setBusy(true);
    status.textContent = "";
    try {
      const res = await fetch(form.action, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout ? AbortSignal.timeout(20000) : undefined,
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok && result.ok) return confirmation(form, payload, result);
      if (res.status === 422 && result.fields) {
        const bad = Object.entries(result.fields)
          .map(([name, msg]) => {
            const unit = form.querySelector(`[data-field="${name}"]`);
            if (unit) showError(unit, msg);
            return unit;
          })
          .filter(Boolean);
        status.textContent = result.message || "Certains champs sont à corriger.";
        if (bad[0]) focusUnit(bad[0]);
        return;
      }
      throw new Error(result.message || `HTTP ${res.status}`);
    } catch (err) {
      console.warn(err);
      status.classList.add("is-error");
      status.innerHTML = `L'envoi n'a pas abouti${navigator.onLine === false ? " : vous semblez hors connexion" : ""}. Vos informations sont conservées : réessayez dans un instant, ou appelez-nous au <a href="tel:${DEALER.phoneIntl}">${DEALER.phone}</a>.`;
    } finally {
      setBusy(false);
    }
  });
}

// ---------------------------------------------------------------- essai : modèle présélectionné

const essai = document.getElementById("form-essai");
if (essai) {
  const select = essai.querySelector('select[name="modele"]');
  const pick = (name) => {
    const opt = [...select.options].find((o) => o.value && (o.value === name || o.textContent === name));
    if (!opt) return;
    select.value = opt.value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
  };
  const asked = new URLSearchParams(location.search).get("essai") ?? essai.dataset.preselect;
  if (asked) pick(asked);

  document.addEventListener("click", (e) => {
    const a = e.target.closest("a[data-essai]");
    if (!a) return;
    e.preventDefault();
    pick(a.dataset.essai);
    const url = new URL(location.href);
    url.searchParams.set("essai", a.dataset.essai);
    url.hash = "essai";
    history.replaceState(history.state, "", url);
    document.getElementById("essai").scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
  });
}
